import { apiAddress } from "../../config/server";
import {
  IAppointment,
  IAppointmentDetail,
  IAppointmentStatusChange,
  ICompletedAppointmentRow,
  IOfficialLoading,
} from "../../types/appointments/Appointment";
import {
  IPublishedAppointment,
  IPublishedAppointmentDetail,
} from "../../types/appointments/PublishedAppointment";
import { IVenue } from "../../types/venue/Venue";
import { ApiBase, GenericTransport } from "./baseApi";

export default class AppointmentsApi extends ApiBase {
  static AppointmentsApiAddress = `${apiAddress}/api/appointments/`;

  static getAppointmentById(
    appointmentId,
    sessionId?: string,
  ): Promise<GenericTransport<IAppointment<IAppointmentDetail>>> {
    // SECURITY: Validate appointment ID format
    if (!appointmentId || typeof appointmentId !== 'string' || !/^[a-fA-F0-9]{24}$/.test(appointmentId)) {
      return Promise.reject(new Error('Invalid appointment ID format'));
    }

    // SECURITY: Validate session ID format if provided
    if (sessionId && (typeof sessionId !== 'string' || !/^[a-fA-F0-9]{24}$/.test(sessionId))) {
      return Promise.reject(new Error('Invalid session ID format'));
    }

    let q = "";
    if (sessionId) q = this.prepareQueryParams({ sessionId });
    return AppointmentsApi.get<IAppointment<IAppointmentDetail>>(
      this.AppointmentsApiAddress + appointmentId + q,
    ).then((resp) => resp.data);
  }

  static getPublishedAppointments(): Promise<{
    appointments: IPublishedAppointment<IPublishedAppointmentDetail>[];
    venues: IVenue[];
  }> {
    return AppointmentsApi.get<
      IPublishedAppointment<IPublishedAppointmentDetail>[]
    >(`${AppointmentsApi.AppointmentsApiAddress}published`).then((resp) => ({
      appointments: resp.data.data || [],
      venues: resp.data.venues || [],
    }));
  }

  static updateAppointmentStatus(
    apId,
    change: Partial<IAppointmentStatusChange>,
  ): Promise<IAppointment<IAppointmentDetail>> {
    // SECURITY: Validate appointment ID format
    if (!apId || typeof apId !== 'string' || !/^[a-fA-F0-9]{24}$/.test(apId)) {
      return Promise.reject(new Error('Invalid appointment ID format'));
    }

    // SECURITY: Validate change object
    if (!change || typeof change !== 'object') {
      return Promise.reject(new Error('Invalid status change data'));
    }

    // SECURITY: Sanitize comment if present
    if (change.comment && typeof change.comment === 'string') {
      // Encode HTML entities to prevent XSS while preserving content
      change.comment = change.comment
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .slice(0, 1000);
    }

    return AppointmentsApi.patch<IAppointment<IAppointmentDetail>>(
      `${AppointmentsApi.AppointmentsApiAddress + apId}/status/`,
      change,
    ).then((resp) => {
      const { data } = resp.data;
      if (!data) throw new Error("Returned appointment was null");
      return data;
    });
  }

  static addAppointmentBonus(
    apId,
    loading: IOfficialLoading,
  ): Promise<IAppointment<IAppointmentDetail>> {
    // SECURITY: Validate appointment ID format
    if (!apId || typeof apId !== 'string' || !/^[a-fA-F0-9]{24}$/.test(apId)) {
      return Promise.reject(new Error('Invalid appointment ID format'));
    }

    // SECURITY: Validate loading data
    if (!loading || typeof loading !== 'object') {
      return Promise.reject(new Error('Invalid loading data'));
    }

    return AppointmentsApi.patch<IAppointment<IAppointmentDetail>>(
      `${AppointmentsApi.AppointmentsApiAddress + apId}/loading`,
      loading,
    ).then((resp) => {
      const { data } = resp.data;
      if (!data) throw new Error("Returned appointment was null");
      return data;
    });
  }

  static getConfirmedAppointments(
    associationId: string,
    date: string,
  ): Promise<GenericTransport<ICompletedAppointmentRow[]>> {
    // SECURITY: Validate association ID format
    if (!associationId || typeof associationId !== 'string' || !/^[a-fA-F0-9]{24}$/.test(associationId)) {
      return Promise.reject(new Error('Invalid association ID format'));
    }

    // SECURITY: Validate date format
    if (!date || typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return Promise.reject(new Error('Invalid date format. Expected YYYY-MM-DD'));
    }

    return AppointmentsApi.get<ICompletedAppointmentRow[]>(
      `${AppointmentsApi.AppointmentsApiAddress}approved/${associationId}/${date}`,
    ).then((resp) => resp.data);
  }

  static getAppointmentsByPerson(personId: string) {
    return AppointmentsApi.get<IAppointment<IAppointmentDetail>[]>(
      `${AppointmentsApi.AppointmentsApiAddress}person/${personId}`,
    ).then((res) => res.data);
  }

  static getActionableAppointments(): Promise<GenericTransport<undefined>> {
    return AppointmentsApi.get<undefined>(
      `${AppointmentsApi.AppointmentsApiAddress}actionable`,
    ).then((res) => res.data);
  }

  static getUnconfirmedAppointments(associationId?: string): Promise<GenericTransport<any>> {
    const params = associationId ? `?associationId=${associationId}` : '';
    return AppointmentsApi.get<any>(
      `${AppointmentsApi.AppointmentsApiAddress}unconfirmed${params}`,
    ).then((res) => res.data);
  }

  static setPaysheetVisibilityStatus(appointmentId: string, show: boolean) {
    const url = `${AppointmentsApi.AppointmentsApiAddress}${appointmentId}/payments/visibility/${show}`;
    return AppointmentsApi.patch<IAppointment<any>>(url, {}).then(
      (resp) => resp.data.data,
    );
  }
}
